import { forwardRef, Ref, useId, useRef } from 'react';
import classnames from 'classnames';

import { IconButtonSize } from '@xemplo/icon-button';
import { DawnCross } from '@xemplo/icons';
import { useMergeRefs } from '@xemplo/ref-utils';

import { DrawerBody } from './body/drawer-body';
import { useDrawer } from './hooks/use-drawer';
import { useDrawerAccessibility } from './hooks/use-drawer-accessibility';
import { useDrawerDimensions } from './hooks/use-drawer-dimensions';
import { useDrawerFocus } from './hooks/use-drawer-focus';
import { useDrawerHeader } from './hooks/use-drawer-header';
import * as S from './drawer.styles';
import { DrawerMode, DrawerProps } from './drawer.types';

export const DrawerTestId = {
  container: 'drawer-container',
  content: 'drawer-content',
  backdrop: 'drawer-backdrop',
};

export const Drawer = forwardRef<HTMLElement, DrawerProps>(
  (props: DrawerProps, ref?: Ref<HTMLElement>) => {
    const { customTop } = props;

    const { drawerState, handleClose, handleCloseClick } = useDrawer();
    const drawerRef = useRef<HTMLElement | null>(null);
    const mergedRefs = useMergeRefs([ref, drawerRef]);

    const { header, body, footer } = drawerState.content;
    const { mode, position, width, headerKeyline, hasBackdrop } = drawerState.config;
    const { style } = useDrawerDimensions({ topOffset: customTop, width });

    useDrawerFocus({ drawerRef: drawerRef, isOpen: drawerState.isOpen });

    const titleId = useId();
    const { headerTitle, headerElement } = useDrawerHeader({ header, mode, titleId });

    const { bodyId, dialogProps } = useDrawerAccessibility({
      hasBackdrop,
      hasBody: !!body,
      isOpen: drawerState.isOpen,
      onClose: handleClose,
      titleId,
      headerTitle,
    });

    return (
      <>
        {hasBackdrop && drawerState.isOpen && (
          <S.Backdrop data-testid={DrawerTestId.backdrop} />
        )}
        <S.DrawerWrapper
          ref={mergedRefs}
          {...dialogProps}
          data-open={drawerState.isOpen ?? false}
          data-position={position}
          data-testid={DrawerTestId.container}
          style={style}
          className={props.className}
        >
          {drawerState.contentVisible && (
            <S.DrawerContentWrapper
              data-testid={DrawerTestId.content}
              $offset={customTop}
            >
              {mode === DrawerMode.Standard && (
                <S.CloseButton
                  id="drawer-close-btn"
                  ariaLabel="Button to close drawer"
                  size={IconButtonSize.Small}
                  naked
                  onClick={handleCloseClick}
                >
                  <DawnCross fontSize={16} />
                </S.CloseButton>
              )}
              <S.DrawerHeaderWrapper
                id="drawer-header"
                className={classnames({ keyline: !!headerKeyline })}
              >
                {headerElement}
              </S.DrawerHeaderWrapper>
              <DrawerBody id={bodyId} headerKeyline={headerKeyline}>
                {body}
              </DrawerBody>
              {mode === DrawerMode.Standard && footer}
            </S.DrawerContentWrapper>
          )}
        </S.DrawerWrapper>
      </>
    );
  },
);

export default Drawer;
